import { calculateCostUsd } from "./costCalculator";

const FEATURES = ["contract_profile_inventory", "contract_profile_selection", "metadata_guess"];
const BRAINS = ["contract_mechanism_inventory_v1", "contract_mechanism_selection_v1", "metadata_guess_v1"];
const USERS = ["user_a39f", "user_b72a", "user_c17x", "user_d91m"];
const CLIENTS = ["client_north", "client_south"];
const MODELS = ["gpt-5.2", "gpt-4o-mini"];

const successExample = {
    log_type: "execution_story",
    service: "archimede",
    timestamp: "2026-04-22T01:00:04.231Z",
    request_id: "req_abc123def456",
    execution_id: "exec_xyz789abc123",
    user_id: "user_789",
    client_id: "client_012",
    brain: "contract_mechanism_inventory_v1",
    brain_version: "v1",
    feature: "contract_profile_inventory",
    status: "success",
    started_at: "2026-04-22T01:00:00.000Z",
    ended_at: "2026-04-22T01:00:04.231Z",
    duration_ms: 4231,
    retry_count: 1,
    llm_call_count: 2,
    models_used: ["gpt-4o-mini"],
    steps: [
        { step: "input_validation", status: "success", started_at: "2026-04-22T01:00:00.000Z", ended_at: "2026-04-22T01:00:00.012Z", duration_ms: 12 },
        { step: "metadata_inference", status: "success", started_at: "2026-04-22T01:00:00.012Z", ended_at: "2026-04-22T01:00:00.152Z", duration_ms: 140 },
        { step: "llm_generation", status: "success", started_at: "2026-04-22T01:00:00.152Z", ended_at: "2026-04-22T01:00:03.352Z", duration_ms: 3200, model: "gpt-4o-mini" },
        { step: "stream_emit", status: "success", started_at: "2026-04-22T01:00:03.352Z", ended_at: "2026-04-22T01:00:04.231Z", duration_ms: 879 },
    ],
    warnings: [],
    errors: [],
    metadata: {
        cards_emitted: 12,
        has_final_event: true,
        input_tokens: 950,
        output_tokens: 550,
        total_tokens: 1500,
    },
};

const failureExample = {
    log_type: "execution_story",
    service: "archimede",
    timestamp: "2026-04-22T01:00:04.500Z",
    request_id: "req_abc123def999",
    execution_id: "exec_xyz789abc777",
    user_id: "user_781",
    client_id: "client_012",
    brain: "review_v1",
    brain_version: "v1",
    feature: "metadata_guess",
    status: "error",
    started_at: "2026-04-22T01:00:00.000Z",
    ended_at: "2026-04-22T01:00:04.500Z",
    duration_ms: 4500,
    retry_count: 3,
    llm_call_count: 3,
    models_used: ["gpt-5.2"],
    steps: [
        { step: "input_validation", status: "success", started_at: "2026-04-22T01:00:00.000Z", ended_at: "2026-04-22T01:00:00.012Z", duration_ms: 12 },
        { step: "routing", status: "success", started_at: "2026-04-22T01:00:00.012Z", ended_at: "2026-04-22T01:00:01.200Z", duration_ms: 1188, model: "gpt-5.2" },
        { step: "review_generation", status: "error", started_at: "2026-04-22T01:00:01.200Z", ended_at: "2026-04-22T01:00:04.500Z", duration_ms: 3300, model: "gpt-5.2" },
    ],
    warnings: [
        { message: "Retry attempt 1/3 due to rate limit", step: "review_generation" },
        { message: "Retry attempt 2/3 due to rate limit", step: "review_generation" },
    ],
    errors: [{ type: "TimeoutError", message: "LLM generation timed out after 45s", step: "review_generation" }],
    metadata: {
        cards_emitted: 0,
        has_final_event: false,
        input_tokens: 900,
        output_tokens: 300,
        total_tokens: 1200,
    },
};

function randomFrom(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

function mkDateWithin48h(offsetHours) {
    const now = Date.now();
    return new Date(now - offsetHours * 60 * 60 * 1000).toISOString();
}

export function buildSyntheticStories(count = 50) {
    const stories = [successExample, failureExample];

    for (let i = 0; i < count; i += 1) {
        const feature = randomFrom(FEATURES);
        const brain = feature === "contract_profile_inventory"
            ? BRAINS[0]
            : feature === "contract_profile_selection"
                ? BRAINS[1]
                : BRAINS[2];
        const model = randomFrom(MODELS);
        const service = i % 2 === 0 ? "archimede" : "verdictia-backend";
        const status = Math.random() > 0.13 ? "success" : "error";
        const inputTokens = Math.floor(400 + Math.random() * 2800);
        const outputTokens = Math.floor(180 + Math.random() * 2200);
        const totalTokens = inputTokens + outputTokens;
        const duration = Math.floor(700 + Math.random() * 6400);
        const started = mkDateWithin48h(Math.random() * 48);
        const ended = new Date(new Date(started).getTime() + duration).toISOString();
        const cost = calculateCostUsd({ inputTokens, outputTokens, model });

        stories.push({
            log_type: "execution_story",
            service,
            timestamp: ended,
            request_id: `req_${i.toString().padStart(3, "0")}_${Math.random().toString(36).slice(2, 8)}`,
            execution_id: `exec_${Math.random().toString(36).slice(2, 10)}`,
            user_id: USERS[i % USERS.length],
            client_id: CLIENTS[i % CLIENTS.length],
            brain,
            brain_version: "v1",
            feature,
            status,
            started_at: started,
            ended_at: ended,
            duration_ms: duration,
            retry_count: status === "success" ? 0 : 1,
            llm_call_count: 1,
            models_used: [model],
            steps: [
                {
                    step: "input_preparation",
                    status: "success",
                    started_at: started,
                    ended_at: new Date(new Date(started).getTime() + Math.floor(duration * 0.12)).toISOString(),
                    duration_ms: Math.floor(duration * 0.12),
                },
                {
                    step: feature.startsWith("contract_profile_") ? "contract_profile_streaming" : "prompt_construction",
                    status,
                    started_at: new Date(new Date(started).getTime() + Math.floor(duration * 0.12)).toISOString(),
                    ended_at: ended,
                    duration_ms: Math.floor(duration * 0.88),
                    model,
                },
            ],
            warnings: status === "success" ? [] : [{ message: "Temporary provider timeout", step: "streaming" }],
            errors: status === "success" ? [] : [{ message: "Model timeout", type: "TimeoutError", step: "streaming" }],
            metadata: {
                user_id: USERS[i % USERS.length],
                client_id: CLIENTS[i % CLIENTS.length],
                input_tokens: inputTokens,
                output_tokens: outputTokens,
                total_tokens: totalTokens,
                cards_emitted: Math.floor(Math.random() * 10),
                estimated_cost_usd: cost,
                has_final_event: status === "success",
                stream_completed: status === "success",
            },
        });
    }

    return stories;
}

export const SEED_EXECUTION_STORIES = buildSyntheticStories(50);
